import type { VideoSeriesDetailType } from "@volgo/core/contract";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useSession } from "../session";
import { Dialog } from "../ui/Dialog";
import { ErrorNote } from "../ui/States";

export async function resizeKeyVisual(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, 1200 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("이 브라우저에서 그림을 처리할 수 없습니다.");
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    for (const quality of [0.85, 0.7, 0.5]) {
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
      if (blob && blob.size <= 1024 ** 2) return blob;
    }
    throw new Error("그림을 1MiB 이하로 줄이지 못했습니다. 더 작은 그림을 선택하세요.");
  } finally {
    bitmap.close();
  }
}
export function SeriesEditor({
  series,
  close,
  saved,
}: {
  series: VideoSeriesDetailType;
  close: () => void;
  saved: () => void;
}) {
  const { client } = useSession();
  const navigate = useNavigate();
  const [title, setTitle] = useState(series.title);
  const [alt, setAlt] = useState(series.altTitle ?? "");
  const [description, setDescription] = useState(series.description);
  const [image, setImage] = useState<File | null>(null);
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const [remove, setRemove] = useState(false);
  return (
    <Dialog label="작품 편집" onClose={close}>
      <form
        className="video-form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            await client.call("videos.seriesUpdate", {
              params: { id: series.id },
              body: { title: title.trim(), altTitle: alt || null, description },
            });
            if (image) await client.seriesImage(series.id, await resizeKeyVisual(image));
            saved();
            close();
          } catch (e) {
            setError(e);
          } finally {
            setBusy(false);
          }
        }}
      >
        <h2>작품 편집</h2>
        <label>
          작품 제목
          <input required maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label>
          다른 이름
          <input maxLength={120} value={alt} onChange={(e) => setAlt(e.target.value)} />
        </label>
        <label>
          작품 설명
          <textarea maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label>
          대표 그림
          <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] ?? null)} />
        </label>
        <p>긴 변 1200px 이하 JPEG로 저장합니다.</p>
        <button type="submit" className="btn btn-pri" disabled={busy || !title.trim()}>
          저장
        </button>
        {series.episodes.length === 0 &&
          (remove ? (
            <>
              <p>빈 작품을 삭제할까요?</p>
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await client.call("videos.seriesDelete", { params: { id: series.id } });
                    navigate("/videos/series");
                  } catch (e) {
                    setError(e);
                    setBusy(false);
                  }
                }}
              >
                작품 삭제 확인
              </button>
            </>
          ) : (
            <button type="button" className="btn" onClick={() => setRemove(true)}>
              작품 삭제
            </button>
          ))}
        {error !== undefined && <ErrorNote error={error} />}
      </form>
    </Dialog>
  );
}
